import ApolloClient from 'apollo-client';
import { PageNode, Scalars } from 'src/generated/webapp_gql';
import { PageType } from './models/program';
import { CurrentPageFragment } from './resolvers';

export type CurrentPageFragmentQuery = Pick<PageNode, 'id' | 'type' | 'parsedAnswerOptions'>;

// NOTE: We plan to move this state handling into a local
// context, this is an intermediate step between Rematch
// and Apollo.

// See DIGIN-577 for additional context.
// https://joinmodernhealth.atlassian.net/browse/DIGIN-577

export const selectAnswerOption = (
    client: ApolloClient<any>,
    args: {
        pageId: Scalars['String'];
        answerIndex: Scalars['Int'];
        customOptionValue?: string;
        isSelected?: boolean;
    },
): boolean => {
    const { pageId, answerIndex, isSelected } = args;

    // Read the page by ID
    const page = client.readFragment<CurrentPageFragmentQuery>({
        id: pageId,
        fragment: CurrentPageFragment,
    });

    // Return `false` if the page or its answers are not found.
    if (!page || !page.parsedAnswerOptions) {
        return false;
    }

    const parsedAnswerOptions = page.parsedAnswerOptions.map(option => ({ ...option }));
    const answer = parsedAnswerOptions[answerIndex];

    // If the answer does not exist, return `false`.
    if (!answer) {
        return false;
    }

    // The default will be true...
    let defaultValue = true;
    if (page.type === PageType.QuestionMultiselect) {
        // For multi-select the default option should be the opposite
        // of the current value.
        defaultValue = !answer.isSelected;
    } else {
        // For all other question types we need to reset
        // the other answers to `false`.
        parsedAnswerOptions?.forEach(parsedAnswer => {
            parsedAnswer.isSelected = false;
        });
    }

    answer.isSelected = isSelected ?? defaultValue;

    // '' is a valid value but not truthy.
    if (typeof args.customOptionValue === 'string') {
        answer.customOptionValue = args.customOptionValue;
    }

    // Update the page by ID
    client.writeFragment({
        id: pageId,
        fragment: CurrentPageFragment,
        data: {
            __typename: 'PageNode',
            ...page,
            parsedAnswerOptions,
        },
    });

    return true;
};
